/* global grecaptcha */
/* eslint-disable */

import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import {
  getAuth,
  getMultiFactorResolver,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { BASE_URL } from "../configs/urls";

export const firebaseConfig = process.env.REACT_APP_FIREBASE
  ? JSON.parse(process.env.REACT_APP_FIREBASE)
  : {
      apiKey: "fake-api-key",
      authDomain: "localhost",
      projectId: "demo-project",
    };

export const app = firebase.initializeApp({
  ...firebaseConfig,
});

// Expose firebase on window for E2E testing (allows custom token auth)
if (typeof window !== "undefined") {
  window.firebase = firebase;
}
let recaptchaVerifier = null;
let _recaptchaWidgetId = null;
let resolver = null;
let globalVerificationId = null;
let _multiFactorHints;

export function refreshCurrentUser() {
  const user = getCurrentUser();
  user.reload();
}

export function logoutFromFirebase() {
  const auth = getAuth();
  signOut(auth).then(() => {
    // Preserve tour completion keys across logout
    const tourKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith("v2_tour_completed")) {
        tourKeys.push({ key, value: localStorage.getItem(key) });
      }
    }
    localStorage.clear();
    sessionStorage.clear();
    // Restore tour completion keys
    tourKeys.forEach(({ key, value }) => {
      localStorage.setItem(key, value);
    });
  });
}
export function getCurrentUser() {
  return app.auth().currentUser;
}

export function isFirebase2FaEnabled() {
  return app.auth().currentUser.multiFactor?.enrolledFactors?.length > 0;
}

export function disable2FA(success) {
  const user = app.auth().currentUser;
  const options = user.multiFactor.enrolledFactors;
  user.multiFactor.unenroll(options[0]).then(() => {
    success();
  });
}

export function firebaseSignUp(email, password) {
  return app.auth().createUserWithEmailAndPassword(email, password);
}

export function isMailVerified() {
  return app.auth().currentUser?.emailVerified;
}

export function verifyEmail() {
  const user = app.auth().currentUser;
  if (!user?.emailVerified) {
    return app.auth().currentUser.sendEmailVerification();
  } else {
    return new Promise((_resolve, reject) => {
      reject(new Error("Email already verified"));
    });
  }
}

export function setupRecaptcha(success, expire) {
  recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
    "recaptcha-container",
    {
      size: "invisible",
      callback: (response) => {
        if (response) {
          success();
        }
      },
      "expired-callback": () => {
        if (expire) expire();
      },
    }
  );
  recaptchaVerifier.render().then((widgetId) => {
    _recaptchaWidgetId = widgetId;
    success();
  });
}

export function enrollSecondFactor(phoneNumber, error, success) {
  const user = app.auth().currentUser;
  user.multiFactor
    .getSession()
    .then((session) => {
      const phoneOpts = {
        phoneNumber,
        session,
      };
      const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
      phoneAuthProvider
        .verifyPhoneNumber(phoneOpts, recaptchaVerifier)
        .then((verificationId) => {
          globalVerificationId = verificationId;
          success();
        })
        .catch((err) => {
          error(err);
        });
    })
    .catch((err) => {
      error(err);
    });
}

export async function verifyOTP(code) {
  const cred = firebase.auth.PhoneAuthProvider.credential(
    globalVerificationId,
    code
  );
  const multiFactorAssertion =
    firebase.auth.PhoneMultiFactorGenerator.assertion(cred);
  const user = app.auth().currentUser;
  await user.multiFactor.enroll(multiFactorAssertion, "phone number");
}

export async function firebaseLogin(
  email,
  password,
  success,
  needOtp,
  errorCallback
) {
  const auth = getAuth();
  signInWithEmailAndPassword(auth, email, password)
    .then((user) => {
      success(user);
    })
    .catch((error) => {
      if (error.code === "auth/multi-factor-auth-required") {
        resolver = resolver = getMultiFactorResolver(auth, error);
        _multiFactorHints = resolver.hints;

        if (
          resolver.hints[0].factorId ===
          firebase.auth.PhoneMultiFactorGenerator.FACTOR_ID
        ) {
          needOtp();
          const phoneInfoOptions = {
            multiFactorHint: resolver.hints[0],
            session: resolver.session,
          };
          const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
          phoneAuthProvider
            .verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier)
            .then((verificationId) => {
              globalVerificationId = verificationId;
            });
        } else {
          errorCallback(error);
        }
      } else {
        errorCallback(error);
      }
    });
}

export async function verifyLoginOTP(code) {
  const cred = firebase.auth.PhoneAuthProvider.credential(
    globalVerificationId,
    code
  );

  const multiFactorAssertion =
    firebase.auth.PhoneMultiFactorGenerator.assertion(cred);

  return resolver.resolveSignIn(multiFactorAssertion);
}
export function isMFAEnabled() {
  const _currentUser = app.auth().currentUser;
}
export function userChangeListener(listener) {
  app.auth().onAuthStateChanged(listener);
}

export const onTokenIdChanged = (listener) => {
  app.auth().onIdTokenChanged(listener);
};

export function getToken() {
  return app.auth().currentUser?.getIdToken();
}

export function sendPasswordReset(email) {
  return sendPasswordResetEmail(getAuth(), email, { url: BASE_URL });
}

export const refreshUser = () => {
  const user = app.auth().currentUser;
  if (user) {
    user.reload();
  }
};

export const imageUploader = async (address, file) => {
  if (file) {
    const storage = getStorage();
    const storageRef = ref(storage, address);

    const uploadSnapshot = await uploadBytes(storageRef, file);
    return getDownloadURL(uploadSnapshot.ref);
  }
};

export const appCheckSetup = () => {
  if (window.location.origin.includes("localhost")) {
    return;
  }
  const siteKey = process.env.REACT_APP_SITE_KEY || "";

  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
};
